import { Link, useParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import SEO from '../components/SEO';
import SiteFooter from '../components/SiteFooter';
import { getCaseStudyBySlug, TECH_LOGOS } from './caseStudiesData';

export default function CaseStudyPage() {
  const { slug } = useParams<{ slug: string }>();
  const study = slug ? getCaseStudyBySlug(slug) : undefined;

  if (!study) {
    return (
      <div className="min-h-screen bg-white font-['Inter']">
        <SEO title="Case study not found" description="This case study does not exist." url="https://www.greyfielddata.com/case-studies" noIndex />
        <Navigation />
        <div className="pt-44 pb-24 text-center px-6">
          <h1 className="text-3xl font-semibold text-gray-900 mb-4">Case study not found</h1>
          <Link to="/case-studies" className="text-[#1a4e94] font-semibold">
            View all case studies
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-['Inter']">
      <SEO
        title={study.seoTitle}
        description={study.cardSummary}
        type="article"
        url={`https://www.greyfielddata.com/case-studies/${study.slug}`}
        image={`https://www.greyfielddata.com${study.heroImage}`}
        keywords={[...study.technologies, study.industry, 'case study', 'data engineering']}
      />
      <Navigation />

      {/* Hero with photography */}
      <section className="relative pt-36 pb-16 md:pt-48 md:pb-20 overflow-hidden bg-[#0a1628]">
        <img
          src={study.heroImage}
          alt={`${study.client} case study`}
          className="absolute inset-0 w-full h-full object-cover object-right"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628] via-[#0a1628]/85 to-[#0a1628]/30" />
        <div className="relative max-w-7xl mx-auto px-6">
          <Link
            to="/case-studies"
            className="inline-flex items-center gap-1.5 text-sm text-gray-300 hover:text-white transition mb-10"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
            </svg>
            All case studies
          </Link>
          <p className="text-sm font-semibold text-blue-300 tracking-[0.2em] uppercase mb-5">
            Case study &nbsp;·&nbsp; {study.service}
          </p>
          <h1 className="text-4xl md:text-6xl font-semibold text-white tracking-tight leading-[1.1] mb-6 max-w-3xl">
            {study.headline}
          </h1>
          <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-2xl mb-12">{study.intro}</p>

          <div className="grid sm:grid-cols-3 gap-4 max-w-4xl">
            {study.results.map((result) => (
              <div
                key={result.label}
                className="bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl p-6"
              >
                <div className="text-4xl font-bold text-white tracking-tight mb-2">{result.value}</div>
                <p className="text-sm text-gray-300 leading-snug">{result.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology strip with logos */}
      <section className="border-b border-gray-100 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-7 flex items-center gap-8 flex-wrap">
          <span className="text-xs font-bold text-gray-400 tracking-[0.15em] uppercase flex-none">Technology</span>
          <div className="flex items-center gap-8 flex-wrap">
            {study.technologies.map((tech) => (
              <span key={tech} className="inline-flex items-center gap-2.5">
                {TECH_LOGOS[tech] && <img src={TECH_LOGOS[tech]} alt={`${tech} logo`} className="h-6 w-6 object-contain" />}
                <span className="text-sm font-semibold text-[#31405A]">{tech}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* At a glance */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 tracking-tight mb-10">
            At a glance
          </h2>
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
            {[
              { label: 'Industry', value: study.glance.industry },
              { label: 'Location', value: study.glance.location },
              { label: 'Challenge', value: study.challenge.split('.')[0] + '.' },
              { label: 'Engagement', value: study.glance.engagement },
              { label: 'Result', value: study.glance.result },
            ].map((row, index) => (
              <div
                key={row.label}
                className={`grid md:grid-cols-[200px_1fr] gap-2 md:gap-8 px-8 py-5 ${
                  index > 0 ? 'border-t border-gray-100' : ''
                }`}
              >
                <span className="text-xs font-bold text-gray-400 tracking-[0.12em] uppercase pt-0.5">{row.label}</span>
                <span className="text-[15px] text-gray-700 leading-relaxed">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The challenge */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-[280px_1fr] gap-8 md:gap-16">
          <p className="text-sm font-bold text-[#1a4e94] tracking-[0.15em] uppercase">The challenge</p>
          <p className="text-xl md:text-2xl text-gray-800 leading-relaxed font-medium max-w-3xl">{study.challenge}</p>
        </div>
      </section>

      {/* What Greyfield did */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-sm font-bold text-[#1a4e94] tracking-[0.15em] uppercase mb-3">Approach</p>
          <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 tracking-tight mb-12">
            What Greyfield did
          </h2>
          <div className="space-y-5">
            {study.approach.map((step, index) => (
              <div
                key={step.title}
                className="bg-white rounded-2xl border border-gray-200 shadow-sm px-8 py-7 flex gap-8 items-start"
              >
                <span className="text-3xl font-bold text-[#1a4e94]/30 flex-none leading-none pt-0.5">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-500 leading-relaxed">{step.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results + testimonial */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-sm font-bold text-[#1a4e94] tracking-[0.15em] uppercase mb-3">Results</p>
          <div className="grid md:grid-cols-[1fr_360px] gap-12 md:gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 tracking-tight mb-8">
                What changed for the client
              </h2>
              <p className="text-xl md:text-2xl text-gray-800 font-medium leading-relaxed">
                {study.impact}
              </p>
              {study.quote && study.quoteAttribution && (
                <blockquote className="border-l-4 border-[#1a4e94] pl-8 mt-10">
                  <p className="text-lg md:text-xl text-gray-700 font-medium leading-relaxed mb-5">
                    &ldquo;{study.quote}&rdquo;
                  </p>
                  <footer className="text-gray-500 text-sm font-medium">
                    {study.quoteAttribution}, {study.client}
                  </footer>
                </blockquote>
              )}
            </div>
            <div className="space-y-4">
              {study.results.map((result) => (
                <div key={result.label} className="bg-[#0a1628] rounded-2xl p-7">
                  <div className="text-4xl font-bold text-emerald-400 tracking-tight mb-2">{result.value}</div>
                  <p className="text-sm text-gray-400 leading-snug">{result.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <p className="text-xl md:text-2xl font-semibold text-gray-900 tracking-tight text-center md:text-left">
            Have a report or data workflow like this?
          </p>
          <a
            href="https://cal.com/linustse/meeting-with-linus"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#0a1628] text-white px-8 py-4 rounded-xl text-base font-semibold hover:bg-[#0f2d54] transition shadow-lg flex-none"
          >
            Discuss a similar project
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
